import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState, type FormEvent } from "react";
import { ArrowLeft, FileLock2, FileText, LockKeyhole, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { AppHeader } from "@/components/site/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  deleteHomeDocument,
  getHomeDocumentUrl,
  getHomeVaultAccess,
  listHomeDocuments,
  listManagedHomes,
  recordHomeDocument,
} from "@/lib/homes.functions";
import { removeUploadedFile, uploadHomeDocument } from "@/lib/uploads";

export const Route = createFileRoute("/_authenticated/home/vault")({
  validateSearch: (search: Record<string, unknown>) => ({ homeId: typeof search['homeId'] === "string" ? search['homeId'] : undefined }),
  head: () => ({
    meta: [
      { title: "Home Vault — Shields" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: HomeVaultPage,
});

const categories = [
  ["certificate", "Certificate"],
  ["warranty", "Warranty"],
  ["manual", "Manual"],
  ["invoice", "Invoice"],
  ["receipt", "Receipt"],
  ["photo", "Photo"],
  ["other", "Other"],
] as const;

function HomeVaultPage() {
  const { homeId: requestedHomeId } = Route.useSearch();
  const client = useQueryClient();
  const homes = useQuery({ queryKey: ["managed-homes"], queryFn: () => listManagedHomes() });
  const [busy, setBusy] = useState(false);
  const [homeId, setHomeId] = useState("");
  const [category, setCategory] = useState<(typeof categories)[number][0]>("certificate");
  const [filter, setFilter] = useState<"all" | "certificate" | "manual" | "photo">("all");
  const effectiveHomeId = homeId || homes.data?.find((h) => h.id === requestedHomeId)?.id || homes.data?.find((h) => h.isActive)?.id || homes.data?.[0]?.id || "";
  const selectedHome = homes.data?.find((home) => home.id === effectiveHomeId);
  const access = useQuery({
    queryKey: ["home-vault-access", effectiveHomeId],
    queryFn: () => getHomeVaultAccess({ data: { homeId: effectiveHomeId } }),
    enabled: Boolean(effectiveHomeId),
  });
  const docs = useQuery({
    queryKey: ["home-documents"],
    queryFn: () => listHomeDocuments(),
    enabled: Boolean(effectiveHomeId && access.data?.entitled),
  });
  const canUpload = access.data?.access === "owner" && selectedHome && !selectedHome.isShared;
  const visibleDocs = useMemo(
    () => (docs.data ?? []).filter((d: any) => {
      if (effectiveHomeId && d.home_id !== effectiveHomeId) return false;
      if (filter === "all") return true;
      if (filter === "certificate") return d.category === "certificate" || d.category === "warranty";
      if (filter === "manual") return d.category === "manual";
      return d.category === "photo";
    }),
    [docs.data, effectiveHomeId, filter],
  );

  async function upload(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!effectiveHomeId || !canUpload) {
      toast.error("You cannot upload documents to this property.");
      return;
    }
    const form = event.currentTarget;
    const values = new FormData(form);
    const file = values.get("file");
    const title = String(values.get("title") || "").trim();
    if (!(file instanceof File) || !file.size) {
      toast.error("Choose a document to upload.");
      return;
    }
    setBusy(true);
    let path: string | null = null;
    try {
      path = await uploadHomeDocument(file);
      const result = await recordHomeDocument({
        data: {
          homeId: effectiveHomeId,
          category,
          title: title || file.name,
          storagePath: path,
          originalName: file.name,
          mimeType: file.type || undefined,
          sizeBytes: file.size,
        },
      });
      if (!result.ok) {
        if (path) await removeUploadedFile("home-documents", path);
        throw new Error(result.error);
      }
      form.reset();
      toast.success("Document added to your Home Vault");
      await client.invalidateQueries({ queryKey: ["home-documents"] });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not upload the document");
    } finally {
      setBusy(false);
    }
  }

  async function openDocument(id: string) {
    try {
      const result = await getHomeDocumentUrl({ data: { documentId: id } });
      if (!result.ok) {
        toast.error(result.error);
        return;
      }
      window.open(result.url, "_blank", "noopener,noreferrer");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not open this document");
    }
  }

  async function removeDocument(id: string) {
    if (busy) return;
    setBusy(true);
    try {
      const result = await deleteHomeDocument({ data: { documentId: id } });
      if (!result.ok) {
        toast.error(result.error);
        return;
      }
      toast.success(result.cleanupPending
        ? "Document removed. Shields will finish storage cleanup automatically."
        : "Document removed");
      await client.invalidateQueries({ queryKey: ["home-documents"] });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not remove this document");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#f7f9fc] pb-24">
      <AppHeader workspace="customer" />
      <main className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6">
        <Link to="/home" className="inline-flex items-center gap-2 text-sm font-bold text-muted-foreground">
          <ArrowLeft className="h-4 w-4" /> My Home
        </Link>

        <header className="mt-5">
          <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-[#c99522]">My Home</p>
          <h1 className="mt-1 text-3xl font-extrabold text-[#061b3a]">Documents</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Search and keep certificates, warranties, manuals, invoices, receipts and job photos with the right property.
          </p>
        </header>

        {homes.isError ? (
          <section className="card-surface mt-6 p-5"><p>We couldn’t load your properties.</p><Button type="button" variant="outline" className="mt-3" onClick={() => homes.refetch()}>Try again</Button></section>
        ) : (homes.data ?? []).length > 1 ? (
          <label className="mt-6 grid gap-1.5 text-sm font-semibold">Property
            <select value={effectiveHomeId} onChange={(event) => setHomeId(event.target.value)} className="min-h-11 rounded-xl border border-input bg-background px-3">
              {(homes.data ?? []).map((home) => <option key={home.id} value={home.id}>{home.label} · {home.postcode}{home.isShared ? " · shared with you" : ""}</option>)}
            </select>
          </label>
        ) : null}

        {homes.isError ? null : homes.isLoading || (effectiveHomeId && access.isLoading) ? (
          <p className="mt-8 text-sm text-muted-foreground">Checking your membership…</p>
        ) : access.isError ? (
          <section className="card-surface mt-8 p-5"><p>We couldn’t check document access for this property.</p><Button type="button" variant="outline" className="mt-3" onClick={() => access.refetch()}>Try again</Button></section>
        ) : requestedHomeId && !homes.data?.some((home) => home.id === requestedHomeId) ? (
          <section className="card-surface mt-8 p-5"><p>This property is not available to your account.</p><Link to="/home" className="btn-secondary-hero mt-3 inline-flex">Your properties</Link></section>
        ) : !effectiveHomeId ? (
          <section className="card-surface mt-8 p-5"><p>Add a property to keep its documents together.</p><Link to="/home" className="btn-secondary-hero mt-3 inline-flex">Add a property</Link></section>
        ) : !access.data?.entitled ? (
          <section className="business-locked mt-8">
            <LockKeyhole />
            <div>
              <h2>Home Vault is included with Shields Home and above</h2>
              <p>Your free plan stays usable for booking and job history. Upgrade only if you want the permanent home-management tools.</p>
              <Link to="/plans">Compare memberships</Link>
            </div>
          </section>
        ) : (
          <>
            {canUpload ? <section className="mt-6 rounded-[1.75rem] border border-[#e1e8f0] bg-white p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <span className="os-module-icon"><Upload /></span>
                <div>
                  <h2 className="text-lg font-extrabold text-[#061b3a]">Upload Document</h2>
                  <p className="text-sm text-muted-foreground">Private files up to 20 MB.</p>
                </div>
              </div>
              <form onSubmit={upload} className="mt-5 grid gap-4">
                <label className="grid gap-1.5 text-sm font-semibold">
                  Type
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value as typeof category)}
                    className="min-h-11 rounded-md border border-input bg-background px-3"
                  >
                    {categories.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                </label>
                <label className="grid gap-1.5 text-sm font-semibold">
                  Title
                  <Input name="title" maxLength={160} placeholder="e.g. Boiler warranty" />
                </label>
                <label className="grid gap-1.5 text-sm font-semibold">
                  File
                  <Input name="file" type="file" required accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.txt,.docx" />
                </label>
                <Button type="submit" disabled={busy || !effectiveHomeId} className="min-h-11">
                  {busy ? "Uploading…" : "Upload Document"}
                </Button>
              </form>
            </section> : selectedHome?.isShared ? (
              <p className="mt-6 rounded-2xl border border-border bg-card p-4 text-sm text-muted-foreground">You can view documents shared with this home. Only its owner can upload or remove files.</p>
            ) : null}

            <section className="mt-7">
              <div className="flex items-center gap-2">
                <FileLock2 className="h-5 w-5 text-primary" />
                <h2 className="text-lg font-extrabold text-[#061b3a]">Your documents</h2>
              </div>
              <div className="mt-3 grid grid-cols-4 gap-1 rounded-2xl border border-[#e1e8f0] bg-white p-1" role="tablist" aria-label="Document type">
                {([
                  ["all","All"],
                  ["certificate","Certificates"],
                  ["manual","Manuals"],
                  ["photo","Photos"],
                ] as const).map(([value,label]) => (
                  <button key={value} type="button" role="tab" aria-selected={filter===value} onClick={() => setFilter(value)} className={"min-h-10 rounded-xl px-2 text-xs font-bold "+(filter===value?"bg-[#061b3a] text-white":"text-[#66758d]")}>
                    {label}
                  </button>
                ))}
              </div>
              {docs.isError ? (
                <div className="mt-4 rounded-xl border border-border p-4"><p className="text-sm text-muted-foreground">We couldn’t load this home's documents.</p><Button type="button" variant="outline" size="sm" className="mt-3" onClick={() => docs.refetch()}>Try again</Button></div>
              ) : docs.isLoading ? (
                <p className="mt-4 text-sm text-muted-foreground">Loading documents…</p>
              ) : visibleDocs.length === 0 ? (
                <p className="mt-4 text-sm text-muted-foreground">No documents stored for this property yet.</p>
              ) : (
                <div className="reference-rows mt-4">
                  {visibleDocs.map((document: any) => (
                    <div key={document.id} className="flex min-h-16 items-center gap-3 rounded-2xl border border-[#e1e8f0] bg-white p-4">
                      <span className="reference-row-icon"><FileText /></span>
                      <button type="button" onClick={() => openDocument(document.id)} className="min-w-0 flex-1 text-left">
                        <strong className="block truncate">{document.title}</strong>
                        <small className="block truncate text-muted-foreground">
                          {document.category.replaceAll("_", " ")} · {document.original_name}
                        </small>
                      </button>
                      {document.ownedByMe && (
                        <button
                          type="button"
                          onClick={() => removeDocument(document.id)}
                          disabled={busy}
                          aria-label={`Delete ${document.title}`}
                          className="rounded-lg p-2 text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}
